/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import PaymentMethod from '@linc-technologies/linced-common/enums/payment-method';
import TransactionType from '@linc-technologies/linced-common/enums/transaction-type';
import ReceiptFormat from '@linc-technologies/linced-common/enums/receipt-format';
import config from 'linced-app/config/environment';
import moment from 'moment';

export default class LineItemController extends Controller {
  @service
  fetch;

  @service
  features;

  @service
  finance;

  @service
  print;

  @service
  toaster;

  @computed('model.transactions.@each.sourceTransaction_id')
  get transactions() {
    return this.model.transactions.filter((item) => !item.sourceTransaction_id);
  }

  @computed('model.transactions.@each.{type,amount}')
  get amountPaid() {
    return this.model.transactions
      .filter((item) => !item.isCancelled)
      .filter((item) => item.type !== TransactionType.APPORTIONMENT)
      .reduce((val, transaction) => {
        switch (transaction.type) {
          case TransactionType.PAYMENT:
            return val + transaction.amount;
          case TransactionType.REFUND:
            return val + transaction.displayAmount;
        }
        return val;
      }, 0)
      .toFixed(2);
  }

  @tracked
  refundAmount = null;

  @action
  setRefundAmount(value) {
    this.refundAmount = value;
  }

  @tracked
  refundComment = '';

  @action
  setRefundComment(value) {
    this.refundComment = value;
  }

  @tracked
  refundDate = moment().format('YYYY-MM-DD');

  @action
  setRefundDate(value) {
    this.refundDate = value;
  }

  @tracked
  selectedTransactions = [];

  @tracked
  sapRefundDialogOpen = false;

  tillDisabled = false;

  @tracked
  editingTransaction = null;

  @tracked
  isEditingTransaction = false;

  PaymentMethod = PaymentMethod;

  TransactionType = TransactionType;

  @tracked
  errorMessage;

  @tracked
  refundDialogOpen;

  @tracked
  refundInProgress;

  @tracked
  refundType;

  @action
  receipt(transaction) {
    this.print.communityReceipt(transaction, ReceiptFormat.EIGHTYMM);
  }

  @action
  toggleRefundDialog() {
    this.errorMessage = '';
    this.toggleProperty('refundDialogOpen');
    if (!this.refundDialogOpen) {
      this.resetRefundForm();
    }
  }

  @action
  setRefundType(newType) {
    this.refundType = newType;
  }

  @action
  refund() {
    this.refundInProgress = true;
    let url = `${config.APP.apiHost}/finance/${config.APP.apiPath}/lineitems/${this.model.item.id}/:refund`;
    let data = {
      comment: this.refundComment,
      date: this.refundDate,
      amount: Math.round(this.refundAmount * 100),
    };
    if (this.features.sapFinance) {
      data.tillId = parseInt(this.get('finance.activeTill.id'), 10);
      data.method = PaymentMethod.CASH;
    }
    let options = {
      data: JSON.stringify(data),
    };
    this.fetch
      .post(url, options)
      .then(() => {
        this.toaster.success('Line Item refunded successfully.');
        this.model.item.reload();
        this.model.transactions.update();
        this.refundDialogOpen = false;
        this.resetRefundForm();
      })
      .catch(() => {
        this.toaster.error('Error refunding Line Item!');
      })
      .then(() => {
        this.refundInProgress = false;
      });
  }

  @action
  editTransaction(transaction) {
    this.isEditingTransaction = true;
    this.editingTransaction = transaction;
  }

  @action
  closeEditTransaction() {
    this.isEditingTransaction = false;
    this.editingTransaction = null;
  }

  @action
  onTransactionDeleted(cancelledTransaction) {
    this.model.transactions.update();
    this.model.item.reload();
    if (!isEmpty(cancelledTransaction)) {
      // Transaction was cancelled, not deleted.  Unselect it if necessary.
      this.selectedTransactions = this.selectedTransactions.filter(
        (t) => t !== cancelledTransaction,
      );
    }
  }

  @action
  toggleTransaction(transaction) {
    if (this.selectedTransactions.includes(transaction)) {
      this.selectedTransactions = this.selectedTransactions.filter(
        (t) => t !== transaction,
      );
    } else {
      this.selectedTransactions = [...this.selectedTransactions, transaction];
    }
  }

  @action
  openSapRefundDialog() {
    this.sapRefundDialogOpen = true;
  }

  @action
  closeSapRefundDialog(refresh) {
    this.sapRefundDialogOpen = false;
    if (refresh) {
      this.model.transactions.update();
      this.model.item.reload();
      this.selectedTransactions = [];
    }
  }

  resetRefundForm() {
    this.refundType = 'direct';
    this.refundAmount = null;
    this.refundComment = '';
    this.refundDate = moment().format('YYYY-MM-DD');
  }

  reset() {
    this.selectedTransactions = [];
    this.resetRefundForm();
  }
}
